import styled from '@emotion/styled';
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Menu from '@base/Menu/Menu';
import { hasActiveSubscriptionToPlatformBatch } from '@models/Me';
import { useDispatch, useSelector } from 'react-redux';
import { getBatchGroupSubscriptions } from '@selectors/auth';
import { isCompletedWithNoVideoUrl, isPostClickable } from '@models/Item';
import PDFDownload from '@cont/Planner/PlannerItemMenu/PDFDownload';
import ItemCard from '@cont/Course/ItemCard';
import ArrowUp from '@icon/Syllabus/ArrowUp';
import Save from '@icon/Save';
import Saved from '@icon/Saved';
import { removeEntityFromSaved, saveEntity } from '@actions/saved';
import pushRoute from '@utils/pushRoute';
import P1 from '@base/P1/P1';
import { savedObjectTypes, BACKEND_API_ERROR } from '@constants/index';
import {
  SAVE_SAVE_CONTENT,
  SAVE_UNSAVE_CONTENT,
  LESSON_DOWNLOAD_PDF,
  VIEW_EDUCATOR
} from '@cont/BatchGroup/constants/events';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import CloudDownload from '@icon/CloudDownload';
import { useSnackbar } from '@stores/snackbar';
import { getIsBatchAccessEnded, getIsMenuHide } from '../utils';
import formatClassData from './utils';

export const MenuItem = styled.li`
  display: grid;
  align-items: center;
  grid-template-columns: ${Spacings.SPACING_5B} 1fr;
  grid-column-gap: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  cursor: pointer;
  transition: background-color 200ms ease-in-out;
  &:hover {
    background-color: var(--color-divider);
  }
`;

export const MenuItemLabel = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--color-divider);
  width: ${`calc(100% - ${Spacings.SPACING_20})`};
  margin: ${Spacings.SPACING_1B} auto;
`;

const StyledItem = styled(ItemCard)`
  .itemContainer {
    box-shadow: unset;
    border-radius: ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_0B};
    margin-bottom: ${({ $isLastElement }) =>
      $isLastElement ? `${Spacings.SPACING_0B}` : `${Spacings.SPACING_6B}`};
    :hover {
      box-shadow: unset;
    }
  }
  .lessonWrapper {
    border: none;
    border-bottom: ${({ $isLastElement }) =>
      $isLastElement
        ? 'none'
        : `${Spacings.SPACING_1} solid var(--color-divider)`};
    padding-bottom: ${({ $isLastElement }) =>
      $isLastElement ? `${Spacings.SPACING_0B}` : `${Spacings.SPACING_6B}`};
  }
  ${mediaQuery.uptoMobile} {
    .lessonWrapper {
      padding-bottom: ${({ $isLastElement }) =>
        $isLastElement ? `${Spacings.SPACING_0B}` : `${Spacings.SPACING_4B}`};
    }
  }
`;
const getIsLessonForRecordedBatch = (slides) => {
  if (!slides) return false;
  return !slides.withoutAnnotations && slides.withAnnotations;
};

const BatchScheduleItem = ({
  item,
  batch,
  isMultipleCourses,
  cardPosition,
  showEducatorForLesson,
  subscription,
  batchGroupUID,
  goalUID,
  lastPrimarySourceSection,
  segmentData,
  isLastElement
}) => {
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();
  const lesson = formatClassData(item);
  const isShowMoreMenu = getIsMenuHide(lesson);
  const slides = lesson.slidesPdf;
  const isLessonForRecordedBatch = getIsLessonForRecordedBatch(slides);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isSaved, setIsSave] = useState(() => lesson.isSaved);
  const batchGroupSubscriptions = useSelector(getBatchGroupSubscriptions);
  const isSubscribedToBatchGroup = hasActiveSubscriptionToPlatformBatch(
    batchGroupSubscriptions,
    batchGroupUID
  );
  const entityType = savedObjectTypes.LESSON;
  const goalSegmentData = useGoalEventProps({
    goalUID
  });

  const handleClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const showActionMenu = (elem) => {
    setMenuAnchorEl(elem);
    setSelectedLesson(lesson);
  };

  const isAccessEnded = getIsBatchAccessEnded(batch?.batchExpiryDate);

  const isLessonClickable = () => {
    if (isCompletedWithNoVideoUrl(lesson) || isAccessEnded) return false;
    return isSubscribedToBatchGroup && isPostClickable(lesson);
  };
  const sendAnalyticsToSegment = (eventName, extraEventProps = {}) => {
    segment.track(eventName, {
      ...segmentData,
      ...goalSegmentData,
      last_primary_source_section: lastPrimarySourceSection,
      ...extraEventProps
    });
  };

  const onItemClick = () => {
    const { type, slug, uid } = lesson;
    if (type === 'post' && isLessonClickable(lesson)) {
      window.open(`/class/${slug}/${uid}`, '_blank');
    }
  };

  const goToEducatorPage = () => {
    sendAnalyticsToSegment(VIEW_EDUCATOR);
    pushRoute('/[authorID]', `/@${selectedLesson.author.username}`);
    setMenuAnchorEl(null);
  };

  const handlePostSave = (res, isSave = false) => {
    const message = isSave
      ? 'Saved Lesson successfully'
      : 'Removed from Saved Lessons successfully';
    if (res?.result) {
      showSnackbar({
        message,
        type: 'success'
      });
      setIsSave(isSave);
    } else {
      showSnackbar({
        message: BACKEND_API_ERROR,
        type: 'error'
      });
    }
    setMenuAnchorEl(null);
  };
  const handleUnSaveItem = () => {
    dispatch(
      removeEntityFromSaved({
        goalUid: goalUID,
        objectUid: selectedLesson.uid,
        entityType,
        key: `${goalUID}-lessons`
      })
    ).then((res) => {
      sendAnalyticsToSegment(SAVE_UNSAVE_CONTENT, {
        save_content_type: 'Lesson',
        save_content_uid: selectedLesson.uid,
        save_content_title: selectedLesson.title
      });
      handlePostSave(res);
    });
  };

  const handleSaveItem = () => {
    dispatch(
      saveEntity({
        goalUid: goalUID,
        objectUid: selectedLesson.uid,
        entityType
      })
    ).then((res) => {
      sendAnalyticsToSegment(SAVE_SAVE_CONTENT, {
        save_content_type: 'Lesson',
        save_content_uid: lesson.uid,
        save_content_title: lesson.name
      });
      handlePostSave(res, true);
    });
  };

  const handlePostPdfDownload = () => {
    sendAnalyticsToSegment(LESSON_DOWNLOAD_PDF, {
      save_content_type: 'Lesson',
      save_content_uid: lesson.uid,
      save_content_title: lesson.name
    });
    handleClose();
  };

  const openWithAnnotation = () => {
    window.open(slides.withAnnotations, '_blank');
    handlePostPdfDownload();
  };

  return (
    <>
      <Menu
        anchorEl={menuAnchorEl}
        onClose={handleClose}
        position="right"
        width="231px"
      >
        {isSubscribedToBatchGroup && (
          <>
            {!isLessonForRecordedBatch && slides && (
              <>
                <PDFDownload
                  slides={slides}
                  handleClose={handlePostPdfDownload}
                />
                <Divider $marginBottom={Spacings.SPACING_6B} />
              </>
            )}
            {isLessonForRecordedBatch && slides && (
              <MenuItem onClick={openWithAnnotation}>
                <CloudDownload
                  size={Spacings.SPACING_5B}
                  color="var(--color-text-secondary)"
                />
                <MenuItemLabel>Download class PDF</MenuItemLabel>
              </MenuItem>
            )}
            {isSaved ? (
              <MenuItem onClick={handleUnSaveItem}>
                <Saved
                  size={Spacings.SPACING_5B}
                  color="var(--color-text-secondary)"
                />
                <MenuItemLabel>Saved</MenuItemLabel>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleSaveItem}>
                <Save
                  size={Spacings.SPACING_5B}
                  color="var(--color-text-secondary)"
                />
                <MenuItemLabel>Save</MenuItemLabel>
              </MenuItem>
            )}
          </>
        )}
        <MenuItem onClick={goToEducatorPage}>
          <ArrowUp
            size={Spacings.SPACING_5B}
            fill="var(--color-text-secondary)"
          />
          <MenuItemLabel>View educator</MenuItemLabel>
        </MenuItem>
      </Menu>
      {isMultipleCourses ? (
        <StyledItem
          key={lesson.uid}
          lesson={lesson}
          forPlus
          cardPosition={cardPosition}
          isBatch
          isSubscribed={isSubscribedToBatchGroup}
          subscription={subscription}
          setAnchorEl={showActionMenu}
          onItemClick={onItemClick}
          showMoreMenu={isShowMoreMenu}
          showBookmark={false}
          goalUID={goalUID}
          showLiveBatch
          isBatchGroup
          $isLastElement={isLastElement}
        />
      ) : (
        <ItemCard
          key={lesson.uid}
          lesson={lesson}
          forPlus
          cardPosition={cardPosition}
          isBatch
          isSubscribed={isSubscribedToBatchGroup}
          subscription={subscription}
          setAnchorEl={showActionMenu}
          onItemClick={onItemClick}
          showMoreMenu={isShowMoreMenu}
          showEducatorForLesson={showEducatorForLesson}
          showBookmark={false}
          goalUID={goalUID}
          showLiveBatch
          isBatchGroup
        />
      )}
    </>
  );
};

export default BatchScheduleItem;
BatchScheduleItem.propTypes = {
  showEducatorForLesson: PropTypes.bool,
  cardPosition: PropTypes.number,
  subscription: PropTypes.objectOf(PropTypes.any),
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  segmentData: PropTypes.objectOf(PropTypes.any),
  isLastElement: PropTypes.bool
};

BatchScheduleItem.defaultProps = {
  showEducatorForLesson: false,
  cardPosition: -1,
  subscription: {},
  segmentData: {},
  isLastElement: false
};
